/**
 * This install's push device (GLOSSARY "Notifying staff"): one install of the
 * brand PWA on one phone or browser that a staff member allowed to receive
 * pushes. Behind the per-tenant flag notifications-v2 (#139); with it off,
 * nothing here acts and the PWA behaves as before.
 *
 * - syncPushDevice, on every open and login: re-registers the current push
 *   subscription so the API refreshes tenant, user agent and last seen. A
 *   push device unseen for 30 days is stale (shared/push/pushDevices.js).
 * - removePushDevice, on logout: removes the push device on the server and
 *   unsubscribes the browser, so a phone that changes hands stops receiving
 *   the previous staff member's notifications.
 *
 * The endpoint last registered is kept in localStorage (per brand origin).
 * When the browser replaced the subscription (pushsubscriptionchange that the
 * service worker could not report, or a lost subscription), the next sync
 * sends it as oldEndpoint so the API drops the old push device.
 *
 * Every function takes its browser and api dependencies as options so the
 * unit tests can run without a service worker.
 */
import { api } from '@/services/api.js'
import brandConfig from '@brand/config.js'

export const NOTIFICATIONS_FLAG = 'notifications-v2'
const ENDPOINT_KEY = 'pushDeviceEndpoint'

let enabledPromise = null

/** notifications-v2 for this build's tenant, fetched once per session; off on any error. */
export function isNotificationsV2Enabled() {
    enabledPromise ??= api.getFlag(NOTIFICATIONS_FLAG)
        .then(res => !!res?.enabled)
        .catch(() => {
            enabledPromise = null
            return false
        })
    return enabledPromise
}

export function base64UrlToUint8Array(base64String) {
    const pad = '='.repeat((4 - (base64String.length % 4)) % 4)
    const raw = atob(base64String.replace(/-/g, '+').replace(/_/g, '/') + pad)
    const out = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
    return out
}

const browserDefaults = () => ({
    apiClient: api,
    nav: typeof navigator !== 'undefined' ? navigator : undefined,
    notification: typeof Notification !== 'undefined' ? Notification : undefined,
    storage: typeof localStorage !== 'undefined' ? localStorage : undefined,
    isEnabled: isNotificationsV2Enabled,
    vapidPublicKey: brandConfig?.pwa?.vapidPublicKey
})

async function pushManagerOf(nav) {
    if (!nav?.serviceWorker || typeof nav.serviceWorker.getRegistration !== 'function') return null
    // getRegistration, not ready: ready never settles when no worker registers.
    const registration = await nav.serviceWorker.getRegistration()
    return registration?.pushManager || null
}

/**
 * Register (or refresh) this push device with the API and remember its endpoint.
 * Used by syncPushDevice and, under notifications-v2, by the subscribe page.
 */
export async function registerPushDevice(subscription, options = {}) {
    const { apiClient, nav, storage } = { ...browserDefaults(), ...options }
    const previous = storage?.getItem(ENDPOINT_KEY)
    const body = {
        subscription: typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription,
        userAgent: nav?.userAgent
    }
    if (previous && previous !== subscription.endpoint) body.oldEndpoint = previous
    await apiClient.post('/api/subscriptions/subscribe', body)
    storage?.setItem(ENDPOINT_KEY, subscription.endpoint)
}

/** The staff member turned pushes off on this install: stop re-subscribing it. */
export function forgetPushDevice(options = {}) {
    const { storage } = { ...browserDefaults(), ...options }
    storage?.removeItem(ENDPOINT_KEY)
}

/**
 * Re-sync this push device on open and login.
 * @returns {Promise<'off'|'unsupported'|'no-permission'|'not-subscribed'|'registered'>}
 */
export async function syncPushDevice(options = {}) {
    const { nav, notification, storage, isEnabled, vapidPublicKey, ...rest } = { ...browserDefaults(), ...options }
    if (!(await isEnabled())) return 'off'
    const pushManager = await pushManagerOf(nav)
    if (!pushManager || !notification) return 'unsupported'
    if (notification.permission !== 'granted') return 'no-permission'

    let subscription = await pushManager.getSubscription()
    // The browser dropped a push device we had registered: subscribe it again.
    // Never for an install whose staff member has not subscribed (or turned it off).
    if (!subscription && storage?.getItem(ENDPOINT_KEY) && vapidPublicKey) {
        subscription = await pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: base64UrlToUint8Array(vapidPublicKey)
        })
    }
    if (!subscription) return 'not-subscribed'

    await registerPushDevice(subscription, { ...rest, nav, storage })
    return 'registered'
}

/**
 * Make this install a push device (#149 banner and block screen): ask for
 * notification permission, subscribe with this brand's VAPID key and register
 * it. A subscription already on the install is replaced, since it may have
 * been made with another key (VAPID rotation); registerPushDevice sends its
 * endpoint as oldEndpoint so the API drops it.
 * @returns {Promise<'unsupported'|'missing-key'|'denied'|'registered'>}
 */
export async function enablePushDevice(options = {}) {
    const { nav, notification, vapidPublicKey, ...rest } = { ...browserDefaults(), ...options }
    const pushManager = await pushManagerOf(nav)
    if (!pushManager || !notification) return 'unsupported'
    if (!vapidPublicKey) return 'missing-key'

    if (notification.permission !== 'granted') {
        const answer = await notification.requestPermission()
        if (answer !== 'granted') return 'denied'
    }

    const existing = await pushManager.getSubscription()
    if (existing) {
        try {
            await existing.unsubscribe()
        } catch (e) {
            console.warn('[push device] could not clear the old subscription', e?.message || e)
        }
    }
    const subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(vapidPublicKey)
    })
    await registerPushDevice(subscription, { ...rest, nav })
    return 'registered'
}

/**
 * The endpoint of this install's push subscription, when it has one and
 * notifications are allowed; null otherwise. What GET /api/subscriptions/status
 * matches against the staff member's push devices.
 */
export async function currentPushEndpoint(options = {}) {
    const { nav, notification } = { ...browserDefaults(), ...options }
    if (notification?.permission !== 'granted') return null
    const pushManager = await pushManagerOf(nav)
    const subscription = pushManager ? await pushManager.getSubscription() : null
    return subscription?.endpoint || null
}

/**
 * Remove this push device on logout. `server: false` when the session is
 * already gone: the browser still unsubscribes, and the API drops the row the
 * first time a push to it answers 404/410.
 *
 * Acts only on an install with a remembered push device, which only
 * registerPushDevice under notifications-v2 writes, so with the flag off
 * logout is unchanged. It reads no flag: a logout forced by a dead session
 * must not wait on another request.
 * @returns {Promise<'not-registered'|'unsupported'|'not-subscribed'|'removed'>}
 */
export async function removePushDevice(options = {}) {
    const { server = true, apiClient, nav, storage } = { ...browserDefaults(), ...options }
    if (!storage?.getItem(ENDPOINT_KEY)) return 'not-registered'
    const pushManager = await pushManagerOf(nav)
    if (!pushManager) return 'unsupported'

    const subscription = await pushManager.getSubscription()
    storage?.removeItem(ENDPOINT_KEY)
    if (!subscription) return 'not-subscribed'

    if (server) {
        try {
            await apiClient.post('/api/subscriptions/unsubscribe', { endpoint: subscription.endpoint })
        } catch (e) {
            console.warn('[push device] server removal failed', e?.message || e)
        }
    }
    try {
        await subscription.unsubscribe()
    } catch (e) {
        console.warn('[push device] browser unsubscribe failed', e?.message || e)
    }
    // Keys the subscribe page keeps for its own status line.
    storage?.removeItem('pushSubscribed')
    storage?.removeItem('pushEndpoint')
    return 'removed'
}
